import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  MenuItem,
  IconButton,
} from "@mui/material";

import { useTheme } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import { iepaProd } from "../../constants/global";
import CloseIcon from "@mui/icons-material/Close";

const ViewUser = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([{ city: "Select City", state: "Select State" }]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [addressId, setAddressId] = useState(null);
  const { selectedRows, users, mode } = location.state || {};

const [isEditing, setIsEditing] = useState(mode === "edit");
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);

  // Get mode and selected user info from location.state

  // Load user data based on mode
  useEffect(() => {
    if (mode === "profile") {
      const loggedUser = JSON.parse(localStorage.getItem("loggedUser"));
      setUserData(loggedUser);
      setUserId(loggedUser?.id);
    } else if (selectedRows && users) {
      const selectedId = selectedRows[0];
      const selected = users.find((u) => u.id === selectedId);
      setUserData(selected);
      setUserId(selectedId);
    }
  }, [mode, selectedRows, users]);
  const initialValues = userData
    ? {
        displayName: userData.contact?.displayName || "",
        username: userData.username || "",
        name: userData.contact?.displayName || "",
        email: userData.contact?.email || "",
        phone: userData.contact?.phone || "",
        address: userData.address?.address_1 || "",
        city: userData.address?.city || "",
        password: userData.passwordHash || "",
        roleId: userData.userRoles?.[0]?.id || "",
      }
    : {
        displayName: "",
        username: "",
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        password: "",
        roleId: "",
      };

  const fetchCityState = async (pincode) => {
    setCities([{ city: "Select City", state: "Select State" }]);
    fetch(`${iepaProd.iepaUserSrvAPI}/iepa/api/city/state/list/pincode/${pincode}`)
      .then((response) => response.json())
      .then((data) => setCities(data))
      .catch((err) => setError("Error fetching city/state: " + err.message));
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleUserFormSubmit = (values, { resetForm }) => {
    const data = {
      username: values.username,
      passwordHash: values.password,
      userRoles: [{ id: values.roleId }],
      id: userId,
      contact: {
        displayName: values.displayName,
        email: values.email,
        phone: values.phone,
      },
      address: { address_1: values.address, city: values.city },
      orgId: "1",
      createdBy: 1,
      companyId: "1",
      updatedBy: 1,
    };

    fetch(`${iepaProd.iepaUserSrvAPI}/user/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => {
        setMessage("User updated successfully!");
        setError(null);
        resetForm();
        setAddressId(data.id);
      })
      .catch(() => {
        setError("Failed to update User. Please try again.");
        setMessage(null);
      });
  };

  const tabContent = [
    {
      label: "User Details",
      content: userData ? (
        <Formik initialValues={initialValues} validationSchema={overviewSchema} onSubmit={handleUserFormSubmit}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <Box mt="16px" display="flex" flexDirection="column">
                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 1 }}
                    required
                    id="username"
                    label="User Name"
                    value={values.username || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.username && Boolean(errors.username)}
                    helperText={touched.username && errors.username}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    id="name"
                    label="Name"
                    value={values.name || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    required
                    id="displayName"
                    label="Display Name"
                    value={values.displayName || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.displayName && Boolean(errors.displayName)}
                    helperText={touched.displayName && errors.displayName}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 1 }}
                    id="email"
                    label="Email"
                    value={values.email || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    id="phone"
                    label="Phone"
                    value={values.phone || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    id="address"
                    label="Address"
                    value={values.address || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 1, maxWidth: 380 }}
                    select
                    id="city"
                    name="city"
                    label="Select City"
                    value={values.city || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  >
                    <MenuItem value="Mangalore">Mangalore</MenuItem>
                    <MenuItem value="Bangalore">Bangalore</MenuItem>
                    <MenuItem value="Pune">Pune</MenuItem>
                  </TextField>
                  <TextField
                    sx={{ flex: 1, maxWidth: 380 }}
                    select
                    id="roleId"
                    name="roleId"
                    label="Select Persona"
                    value={values.roleId || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.roleId && Boolean(errors.roleId)}
                    helperText={touched.roleId && errors.roleId}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  >
                    <MenuItem value="9">Admin</MenuItem>
                    <MenuItem value="6">Back Office</MenuItem>
                    <MenuItem value="5">Field Agent</MenuItem>
                    <MenuItem value="7">Staff</MenuItem>
                    <MenuItem value="8">Payment Admin</MenuItem>
                  </TextField>
                  <TextField
                    sx={{ flex: 1, maxWidth: 380 }}
                    id="password"
                    name="password"
                    label="Change Password"
                    type="password"
                    value={values.password || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    margin="normal"
                    inputProps={{ readOnly: !isEditing }}
                  />
                </Box>
              </Box>
              {message && <div style={{ color: "green" }}>{message}</div>}
              {error && <div style={{ color: "red" }}>{error}</div>}
              {isEditing && (
              <Box display="flex" gap={2} mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isEditing}
                  sx={{ backgroundColor: colors.blueAccent[700], color: "#fff" }}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  disabled={!isEditing}
                  sx={{ backgroundColor: colors.blueAccent[700], color: "#fff" }}
                  onClick={() => {
                    resetForm();
                    setIsEditing(false);
                  }}
                >
                  Reset
                </Button>
              </Box>
              )}
            </form>
          )}
        </Formik>
      ) : (
        <Typography>Loading...</Typography>
      ),
    },
  ];

  const handleClose = () => navigate("/UserList");

  return (
    <Box m="20px">
      <Header title="User Details" />
      <Box display="flex" justifyContent="flex-end" mb="20px">
        <IconButton onClick={handleClose} sx={{ position: "absolute", top: 10, right: 10 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: 16, mr: 1 }}>Close</Typography>
          <CloseIcon />
        </IconButton>
       
      </Box>
      <Card>  
        <CardContent>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabContent.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                sx={{ color: activeTab === index ? "black" : colors.blueAccent[700], fontWeight: activeTab === index ? "bold" : "normal" }}
              />
            ))}
          </Tabs>
          <Box mt="20px">{tabContent[activeTab].content}</Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const overviewSchema = yup.object().shape({
  username: yup.string().required("Username is required"),
  name: yup.string().required("Name is required"),
  email: yup.string().required("Email is required"),
});

export default ViewUser;
